import {
  ExpertSystemEquipmentType,
  MtmKpiValue,
  WeightedAverageValue,
  YearlyKpiValue
} from '@domain/expert-system'
import {ExpertSystemHierarchyNode, ExpertSystemHierarchyNodeType} from '@hconnect/common/types'
import {findNodeInHierarchy} from '@hconnect/common/utils'

import {MONTH_MAP} from '../consts'
import {HierarchyTableNode, MonthDataColumns} from '../types'

import {isKilnOrMillLevel} from './hierarchy.utils'

export function mapAndFilterHierarchy(
  items: ExpertSystemHierarchyNode[],
  equipmentType: ExpertSystemEquipmentType,
  dataWithYearPeriod?: YearlyKpiValue[],
  dataWithMonthPeriod?: MonthDataColumns,
  weightedAverages?: WeightedAverageValue[]
): HierarchyTableNode[] {
  if (!items) return []
  return items
    .filter(
      (node) =>
        !(
          (node.type === ExpertSystemHierarchyNodeType.KilnKpi &&
            equipmentType === ExpertSystemEquipmentType.Mill) ||
          (node.type === ExpertSystemHierarchyNodeType.MillKpi &&
            equipmentType === ExpertSystemEquipmentType.Kiln)
        )
    )
    .map(({children, id, ...rest}) => {
      const matched = dataWithYearPeriod?.find((d: YearlyKpiValue) => d.hierarchyId === id)
      const weightedAvg = weightedAverages?.find((w) => w.hierarchyId === id)
      return {
        ...rest,
        id,
        ...(dataWithMonthPeriod?.[id] ?? {}),
        subItems: mapAndFilterHierarchy(
          children,
          equipmentType,
          dataWithYearPeriod,
          dataWithMonthPeriod,
          weightedAverages
        ),
        average: matched?.value,
        averageError: matched?.kpiCalculationError,
        averageIsDirty: matched?.isDirty,
        weightedAverage: weightedAvg?.value,
        weightedAverageError: weightedAvg?.kpiCalculationError,
        weightedAverageIsPartial: weightedAvg?.isPartial
      }
    })
}

export function mapMonthsData(data: MtmKpiValue[]): MonthDataColumns {
  const result: MonthDataColumns = Object.create(null)
  data.forEach((item) => {
    const {hierarchyId, month, ...rest} = item
    const monthKey = MONTH_MAP[month.split('-')[1]]
    if (!result[hierarchyId]) result[hierarchyId] = {}
    result[hierarchyId][monthKey] = {
      ...rest,
      value: rest.value && Math.round(rest.value)
    }
  })
  return result
}

const LEVELS: Record<ExpertSystemHierarchyNodeType, number> = {
  [ExpertSystemHierarchyNodeType.Global]: 0,
  [ExpertSystemHierarchyNodeType.Area]: 1,
  [ExpertSystemHierarchyNodeType.Region]: 2,
  [ExpertSystemHierarchyNodeType.Country]: 3,
  [ExpertSystemHierarchyNodeType.Plant]: 4,
  [ExpertSystemHierarchyNodeType.KilnKpi]: 5,
  [ExpertSystemHierarchyNodeType.MillKpi]: 5
}
const getHierarchyLevel = (type: ExpertSystemHierarchyNodeType): number => LEVELS[type] ?? 999

function getPathToNode(
  root: ExpertSystemHierarchyNode,
  targetId: string
): ExpertSystemHierarchyNode[] | undefined {
  if (root.id === targetId) return [root]
  for (const child of root.children || []) {
    const childPath = getPathToNode(child, targetId)
    if (childPath) return [root, ...childPath]
  }
}

function getAncestorAtLevel(
  root: ExpertSystemHierarchyNode,
  targetId: string,
  level: number
): ExpertSystemHierarchyNode | undefined {
  const path = getPathToNode(root, targetId)
  if (!path) return undefined
  return path.find((n) => getHierarchyLevel(n.type) === level)
}

function pruneSubtreeToReadableDescendants(
  node: ExpertSystemHierarchyNode,
  readableIds: Set<string>
): ExpertSystemHierarchyNode | undefined {
  const selfReadable = readableIds.has(node.id)
  const prunedChildren = (node.children || [])
    .map((c) => pruneSubtreeToReadableDescendants(c, readableIds))
    .filter((c): c is ExpertSystemHierarchyNode => !!c)

  if (selfReadable || prunedChildren.length) {
    return {
      ...node,
      children: prunedChildren
    }
  }
  return undefined
}

export function mergeReadableRootsWithHierarchy(
  fullHierarchy?: ExpertSystemHierarchyNode,
  readableRoots?: ExpertSystemHierarchyNode[]
): ExpertSystemHierarchyNode[] {
  if (!readableRoots || !fullHierarchy || readableRoots.length === 0) return []
  if (readableRoots[0].type === ExpertSystemHierarchyNodeType.Global) return fullHierarchy.children

  const highestLevel = Math.min(...readableRoots.map((r) => getHierarchyLevel(r.type)))
  const readableIds = new Set(readableRoots.map((r) => r.id))

  const ancestorMap = new Map<string, ExpertSystemHierarchyNode>()

  for (const readableRoot of readableRoots) {
    const level = getHierarchyLevel(readableRoot.type)

    if (level === highestLevel) {
      const fullNode = findNodeInHierarchy(readableRoot.id, fullHierarchy)
      if (fullNode && !ancestorMap.has(fullNode.id)) {
        ancestorMap.set(fullNode.id, fullNode)
      }
    } else {
      const ancestor = getAncestorAtLevel(fullHierarchy, readableRoot.id, highestLevel)
      if (ancestor && !ancestorMap.has(ancestor.id)) {
        ancestorMap.set(ancestor.id, ancestor)
      }
    }
  }

  const results: ExpertSystemHierarchyNode[] = []
  for (const ancestor of ancestorMap.values()) {
    if (readableIds.has(ancestor.id)) {
      results.push(ancestor)
    } else {
      const pruned = pruneSubtreeToReadableDescendants(ancestor, readableIds)
      if (pruned) results.push(pruned)
    }
  }

  return results
}

export function getKpisFromHierarchy(
  node: ExpertSystemHierarchyNode,
  equipmentType: ExpertSystemEquipmentType,
  result: ExpertSystemHierarchyNode[] = [],
  found: boolean = false
) {
  if (!node) return result

  if (node.type === ExpertSystemHierarchyNodeType.Plant) {
    node.children?.forEach((child) => getKpisFromHierarchy(child, equipmentType, result, true))
    return result
  }

  if (isKilnOrMillLevel(node.type) && found) result.push(node)

  node.children?.forEach((child) => getKpisFromHierarchy(child, equipmentType, result, found))
  return result
}

export function getKpisFromTableNode(
  node: HierarchyTableNode,
  result: HierarchyTableNode[] = [],
  found: boolean = false
) {
  if (!node) return result

  if (node.type === ExpertSystemHierarchyNodeType.Plant) {
    node.subItems?.forEach((child) => getKpisFromTableNode(child, result, true))
    return result
  }

  const isSelectableKpi = isKilnOrMillLevel(node.type)

  if (isSelectableKpi && found) result.push(node)

  node.subItems?.forEach((child) => getKpisFromTableNode(child, result, found))
  return result
}
